/**
 * FacultyPayoutsPage - View payout history and earnings
 */

import { useState, useEffect } from 'react';
import FacultyLayout from './FacultyLayout';
import {
  getPayouts,
  getStripeConnectStatus,
  getStripeDashboardUrl,
  formatCurrency,
  formatPayoutStatus,
  getPayoutStatusColor,
  type PayoutsSummary,
  type StripeConnectStatus,
} from '../../api/faculty';

export default function FacultyPayoutsPage() {
  const [payoutData, setPayoutData] = useState<PayoutsSummary | null>(null);
  const [stripeStatus, setStripeStatus] = useState<StripeConnectStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadData();
  }, [page]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [payouts, stripe] = await Promise.all([
        getPayouts({ page, limit: 20 }),
        getStripeConnectStatus().catch(() => null),
      ]);
      setPayoutData(payouts);
      setStripeStatus(stripe);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payouts');
    } finally {
      setIsLoading(false);
    }
  };

  const openStripeDashboard = async () => {
    try {
      const { url } = await getStripeDashboardUrl();
      window.open(url, '_blank');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open Stripe dashboard');
    }
  };

  if (isLoading && !payoutData) {
    return (
      <FacultyLayout>
        <div className="max-w-4xl mx-auto">
          <div className="h-8 bg-warm-100 rounded w-1/3 animate-pulse mb-8" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="card animate-pulse">
                <div className="h-4 bg-warm-100 rounded w-1/2 mb-2" />
                <div className="h-8 bg-warm-100 rounded w-2/3" />
              </div>
            ))}
          </div>
        </div>
      </FacultyLayout>
    );
  }

  if (error && !payoutData) {
    return (
      <FacultyLayout>
        <div className="max-w-4xl mx-auto">
          <div className="card text-center py-12">
            <p className="text-danger-600 mb-4">{error}</p>
            <button onClick={loadData} className="btn-primary">
              Try Again
            </button>
          </div>
        </div>
      </FacultyLayout>
    );
  }

  return (
    <FacultyLayout>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900 mb-2">
              Payouts
            </h1>
            <p className="text-warm-600">
              View your earnings and payout history.
            </p>
          </div>
          {stripeStatus?.connected && (
            <button
              onClick={openStripeDashboard}
              className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors"
            >
              Open Stripe Dashboard
            </button>
          )}
        </div>

        {/* Stripe Connection Status */}
        {stripeStatus && !stripeStatus.payouts_enabled && (
          <div className="card mb-8 bg-warning-50 border-warning-200">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-warning-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h3 className="font-semibold text-warning-800">Complete Your Stripe Setup</h3>
                <p className="text-sm text-warning-700 mt-1">
                  Your Stripe account is not fully set up. Complete your onboarding to receive payments.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        {payoutData && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="card">
              <p className="text-sm text-warm-500 mb-1">Total Earnings</p>
              <p className="text-2xl font-bold text-warm-900">
                {formatCurrency(payoutData.total_earnings_cents)}
              </p>
            </div>
            <div className="card">
              <p className="text-sm text-warm-500 mb-1">Pending</p>
              <p className="text-2xl font-bold text-warning-600">
                {formatCurrency(payoutData.pending_cents)}
              </p>
            </div>
            <div className="card">
              <p className="text-sm text-warm-500 mb-1">Paid Out</p>
              <p className="text-2xl font-bold text-success-600">
                {formatCurrency(payoutData.paid_cents)}
              </p>
            </div>
          </div>
        )}

        {/* Payout History */}
        <section>
          <h2 className="text-xl font-display font-bold text-warm-900 mb-4">
            Payout History
          </h2>

          {!payoutData || payoutData.payouts.length === 0 ? (
            <div className="card text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warm-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-warm-900 mb-2">No Payouts Yet</h3>
              <p className="text-warm-600">
                Your payout history will appear here after you teach your first class.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {payoutData.payouts.map((payout) => (
                  <div key={payout.id} className="card">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <p className="font-semibold text-warm-900">
                            {formatCurrency(payout.amount_cents)}
                          </p>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPayoutStatusColor(payout.status)}`}>
                            {formatPayoutStatus(payout.status)}
                          </span>
                        </div>
                        <p className="text-sm text-warm-600">
                          {payout.description}
                        </p>
                        {payout.show_name && payout.class_date && (
                          <p className="text-sm text-warm-500 mt-1">
                            {payout.show_name} • {new Date(payout.class_date + 'T12:00:00').toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                            {payout.studio_name && ` • ${payout.studio_name}`}
                          </p>
                        )}
                      </div>
                      <div className="text-right text-sm">
                        {payout.status === 'paid' ? (
                          <p className="text-success-600">
                            Arrived {new Date(payout.arrival_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                            })}
                          </p>
                        ) : payout.status === 'in_transit' ? (
                          <p className="text-primary-600">
                            Expected {new Date(payout.arrival_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                            })}
                          </p>
                        ) : (
                          <p className="text-warm-500">
                            Created {new Date(payout.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {payoutData.pagination.total_pages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-warm-600">
                    Page {page} of {payoutData.pagination.total_pages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(payoutData.pagination.total_pages, p + 1))}
                    disabled={page === payoutData.pagination.total_pages}
                    className="px-3 py-1 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </section>

        {/* Tax Info Notice */}
        <div className="mt-8 p-4 bg-warm-50 rounded-lg">
          <h3 className="text-sm font-medium text-warm-700 mb-2">Tax Information</h3>
          <p className="text-sm text-warm-600">
            You'll receive a 1099 form from Stripe for tax purposes if you earned $600 or more during the calendar year.
            Tax information (W-9) is collected and managed securely through your Stripe account.
          </p>
        </div>
      </div>
    </FacultyLayout>
  );
}
